import { endSession } from "@/analytics/client";

export interface AuthUser {
  id: number | string;
  email: string;
  company_id?: number | string | null;
  name?: string | null;
  /** DB-owned roles (V15/V16) surfaced by /auth/me. */
  roles?: string[];
  /** DB-owned permissions (union across the user's roles) for UI gating.
   *  Cosmetic only — enforcement is server-side in the PDP. */
  permissions?: string[];
  /** Platform superadmin (cross-company). */
  is_platform_admin?: boolean;
  needs_onboarding?: boolean;
  /** Subscription status: active, past_due, canceled, unpaid, sponsored, or null (no company). */
  subscription_status?: string | null;
  /** Server-authoritative onboarding step: context, workspace, plan, tools, done. */
  onboarding_step?: string;
}

/** True iff the user holds `permission`. Platform admins pass everything.
 *  UI gating only — the backend PDP is the real enforcement boundary. */
export function hasPermission(
  user: AuthUser | null | undefined,
  permission: string,
): boolean {
  if (!user) return false;
  if (user.is_platform_admin) return true;
  return (user.permissions ?? []).includes(permission);
}

export async function checkAuth(apiBase: string): Promise<AuthUser | null> {
  try {
    const res = await fetch(`${apiBase}/auth/me`, { credentials: "include" });
    if (!res.ok) return null;
    return (await res.json()) as AuthUser;
  } catch {
    return null;
  }
}

export function loginRedirectUrl(apiBase: string): string {
  return `${apiBase}/auth/login`;
}

export async function logout(apiBase: string): Promise<void> {
  // First, while the page is still alive: the redirect below unloads it.
  // Guarded here too, so no analytics failure can block a logout.
  try {
    endSession("logout");
  } catch (err) {
    console.warn("[auth] ending the analytics session failed", err);
  }
  // Clears our session cookies AND returns the IdP end-session URL. We must
  // redirect the browser to it — otherwise BetterAuth's SSO session stays alive
  // and the next login silently re-authenticates the same user.
  let logoutUrl: string | null = null;
  try {
    const r = await fetch(`${apiBase}/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
    const body = (await r.json().catch(() => ({}))) as { logout_url?: string | null };
    logoutUrl = body?.logout_url ?? null;
  } catch {
    // ignore — still attempt the IdP logout redirect below if we have a URL
  }
  if (logoutUrl) {
    window.location.assign(logoutUrl);
  }
}

/** Rotate the session from the refresh cookie (BFF /auth/refresh). Returns true
 *  on success. De-duped against concurrent callers — see auth/refresh.ts. */
export { refresh } from "./refresh";
